import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLazyQuery, useMutation } from "@apollo/client"
import { RootState, AppDispatch } from "../state/store"
import {
  setDefinitionsLoading,
  setDefinitions,
  setDefinitionsError,
  setResolvedLoading,
  setResolved,
  setResolvedError,
  setGlobalLoading,
  setGlobal,
  setGlobalError,
  setOverridesLoading,
  setOverrides,
  setOverridesError,
  updateResolvedFeature,
  updateGlobalFeature,
  addOrganizationOverride,
  removeOrganizationOverride,
  clearOrganizationData,
  selectIsFeatureEnabled,
  selectFeatureState,
  selectFeatureToggles,
  selectResolvedFeatureToggles,
  ResolvedFeatureToggle,
} from "../state/featureTogglesSlice"
import { useActiveOrganization } from "../auth/AuthProvider"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

// Type aliases for convenience - using the actual GraphQL generated types
type SetGlobalFeatureToggleInput = gqlTypes.SetGlobalFeatureToggleInput
type SetOrganizationFeatureOverrideInput = gqlTypes.SetOrganizationFeatureOverrideInput
type FeatureToggleDefinitionGQL = gqlTypes.GetFeatureToggleDefinitionsQuery["getFeatureToggleDefinitions"][0]
type ResolvedFeatureToggleGQL = gqlTypes.GetOrganizationFeatureTogglesQuery["getOrganizationFeatureToggles"][0]
type GlobalFeatureToggleGQL = gqlTypes.GetGlobalFeatureTogglesQuery["getGlobalFeatureToggles"][0]
type OrganizationFeatureOverrideGQL =
  gqlTypes.GetOrganizationFeatureOverridesQuery["getOrganizationFeatureOverrides"][0]

export interface UseFeatureTogglesReturn {
  // Feature state checking
  isEnabled: (featureKey: string) => boolean
  getFeatureState: (featureKey: string) => ResolvedFeatureToggle | undefined

  // Data
  definitions: ReturnType<typeof selectFeatureToggles>["definitions"]
  resolved: ReturnType<typeof selectFeatureToggles>["resolved"]
  global: ReturnType<typeof selectFeatureToggles>["global"]
  overrides: ReturnType<typeof selectFeatureToggles>["overrides"]

  // Loading states
  loading: boolean
  definitionsLoading: boolean
  resolvedLoading: boolean
  globalLoading: boolean
  overridesLoading: boolean

  // Error states
  error: string | null
  definitionsError: string | null
  resolvedError: string | null
  globalError: string | null
  overridesError: string | null

  // Actions
  refreshDefinitions: () => void
  refreshResolved: () => void
  refreshGlobal: () => void
  refreshOverrides: (organizationId?: string) => void
  refreshAdminData: () => void
  refreshUserFeatures: () => void
  setGlobalToggle: (input: SetGlobalFeatureToggleInput) => Promise<void>
  setOrganizationOverride: (input: SetOrganizationFeatureOverrideInput, organizationId?: string) => Promise<void>
  removeOverride: (featureKey: string, organizationId?: string) => Promise<void>

  // Convenience methods
  toggleGlobalFeature: (featureKey: string, enabled: boolean) => Promise<void>
  toggleOrganizationFeature: (featureKey: string, enabled: boolean, organizationId?: string) => Promise<void>
}

export const useFeatureToggles = (): UseFeatureTogglesReturn => {
  const dispatch = useDispatch<AppDispatch>()
  const { activeOrganization } = useActiveOrganization()

  // Refs to store lazy query functions for stable references
  const fetchDefinitionsRef = useRef<(() => Promise<any>) | undefined>(undefined)
  const fetchGlobalRef = useRef<(() => Promise<any>) | undefined>(undefined)
  const fetchUserFeatureStatesRef = useRef<(() => Promise<any>) | undefined>(undefined)

  // Selectors
  const featureToggles = useSelector(selectFeatureToggles)
  const {
    definitions,
    definitionsLoading,
    definitionsError,
    resolved,
    resolvedLoading,
    resolvedError,
    global,
    globalLoading,
    globalError,
    overrides,
    overridesLoading,
    overridesError,
  } = featureToggles

  // Helper functions
  const isEnabled = useCallback((featureKey: string) => {
    return useSelector(selectIsFeatureEnabled(featureKey))
  }, [])

  const getFeatureState = useCallback((featureKey: string) => {
    return useSelector(selectFeatureState(featureKey))
  }, [])

  // GraphQL queries
  const [fetchDefinitions, { loading: lzDefinitionsLoading }] = useLazyQuery(
    gqlTypes.GetFeatureToggleDefinitionsDocument,
    {
      onCompleted: useCallback(
        (data: any) => {
          // Transform GraphQL types to Redux types
          const definitions = (data.getFeatureToggleDefinitions || []).map((def: FeatureToggleDefinitionGQL) => ({
            id: def.id,
            featureKey: def.featureKey,
            name: def.name,
            description: def.description,
            category: def.category,
            defaultEnabled: def.defaultEnabled,
            allowOrgOverride: def.allowOrgOverride,
            createdAt: def.createdAt,
            updatedAt: def.updatedAt,
          }))
          dispatch(setDefinitions(definitions))
        },
        [dispatch]
      ),
      onError: useCallback(
        (error: any) => {
          console.error("fetchDefinitions onError:", error)
          dispatch(setDefinitionsError(error.message))
        },
        [dispatch]
      ),
      fetchPolicy: "cache-and-network",
    }
  )

  // Store function reference for stable access
  fetchDefinitionsRef.current = fetchDefinitions

  useEffect(() => {
    dispatch(setDefinitionsLoading(lzDefinitionsLoading))
  }, [lzDefinitionsLoading, dispatch])

  // Optimized single query for user feature states
  const [fetchUserFeatureStates, { loading: lzUserFeatureStates }] = useLazyQuery(
    gqlTypes.GetUserFeatureStatesDocument,
    {
      onCompleted: (data: any) => {
        // Transform GraphQL types to Redux types
        const resolved = (data.getUserFeatureStates || []).map((toggle: any) => ({
          featureKey: toggle.featureKey,
          name: toggle.name,
          description: toggle.description,
          enabled: toggle.enabled,
          source: toggle.source as "organization" | "global" | "default",
          allowOrgOverride: toggle.allowOrgOverride,
          category: toggle.category,
        }))
        dispatch(setResolved(resolved))
      },
      onError: (error: any) => {
        dispatch(setResolvedError(error.message))
      },
      fetchPolicy: "cache-and-network",
    }
  )

  // Store function reference for stable access
  fetchUserFeatureStatesRef.current = fetchUserFeatureStates

  useEffect(() => {
    dispatch(setResolvedLoading(lzUserFeatureStates))
  }, [lzUserFeatureStates, dispatch])

  const [fetchResolved] = useLazyQuery(gqlTypes.GetOrganizationFeatureTogglesDocument, {
    onCompleted: (data: any) => {
      // Transform GraphQL types to Redux types
      const resolved = (data.getOrganizationFeatureToggles || []).map((toggle: ResolvedFeatureToggleGQL) => ({
        featureKey: toggle.featureKey,
        name: toggle.name,
        description: toggle.description,
        enabled: toggle.enabled,
        source: toggle.source as "organization" | "global" | "default",
        allowOrgOverride: toggle.allowOrgOverride,
        category: toggle.category,
      }))
      dispatch(setResolved(resolved))
    },
    onError: (error: any) => {
      dispatch(setResolvedError(error.message))
    },
    fetchPolicy: "cache-and-network",
  })

  const [fetchGlobal, { loading: lzGlobalLoading }] = useLazyQuery(gqlTypes.GetGlobalFeatureTogglesDocument, {
    onCompleted: useCallback(
      (data: any) => {
        // Transform GraphQL types to Redux types
        const global = (data.getGlobalFeatureToggles || []).map((toggle: GlobalFeatureToggleGQL) => ({
          featureDefinitionId: toggle.featureDefinitionId,
          featureKey: toggle.featureKey,
          name: toggle.name || "", // Provide default if undefined
          enabled: toggle.enabled,
          updatedAt: toggle.updatedAt,
          updatedBy: toggle.updatedBy || undefined,
        }))
        dispatch(setGlobal(global))
      },
      [dispatch]
    ),
    onError: useCallback(
      (error: any) => {
        console.error("fetchGlobal onError:", error)
        dispatch(setGlobalError(error.message))
      },
      [dispatch]
    ),
    fetchPolicy: "cache-and-network",
  })

  // Store function reference for stable access
  fetchGlobalRef.current = fetchGlobal

  useEffect(() => {
    dispatch(setGlobalLoading(lzGlobalLoading))
  }, [lzGlobalLoading, dispatch])

  const [fetchOverrides, { loading: lzOverridesLoading }] = useLazyQuery(
    gqlTypes.GetOrganizationFeatureOverridesDocument,
    {
      onCompleted: (data: any) => {
        // Transform GraphQL types to Redux types
        const overrides = (data.getOrganizationFeatureOverrides || []).map(
          (override: OrganizationFeatureOverrideGQL) => ({
            id: override.id,
            organizationId: override.organizationId,
            featureDefinitionId: override.featureDefinitionId,
            featureKey: override.featureKey,
            name: override.name || "", // Provide default if undefined
            enabled: override.enabled,
            createdAt: override.createdAt,
            updatedAt: override.updatedAt,
            updatedBy: override.updatedBy || undefined,
          })
        )
        dispatch(setOverrides(overrides))
      },
      onError: (error: any) => {
        dispatch(setOverridesError(error.message))
      },
      fetchPolicy: "cache-and-network",
    }
  )

  useEffect(() => {
    dispatch(setOverridesLoading(lzOverridesLoading))
  }, [lzOverridesLoading, dispatch])

  // GraphQL mutations
  const [setGlobalFeatureToggleMutation] = useMutation(gqlTypes.SetGlobalFeatureToggleDocument, {
    onCompleted: (data: any) => {
      const result = data.setGlobalFeatureToggle
      dispatch(
        updateGlobalFeature({
          featureKey: result.featureKey,
          enabled: result.enabled,
        })
      )
      // Also update resolved state if this affects current organization
      dispatch(
        updateResolvedFeature({
          featureKey: result.featureKey,
          enabled: result.enabled,
          source: "global",
        })
      )
    },
    onError: (error: any) => {
      console.error("Failed to set global feature toggle:", error)
    },
  })

  const [setOrganizationFeatureOverrideMutation] = useMutation(gqlTypes.SetOrganizationFeatureOverrideDocument, {
    onCompleted: (data: any) => {
      const result = data.setOrganizationFeatureOverride
      // Transform GraphQL type to Redux type
      const override = {
        id: result.id,
        organizationId: result.organizationId,
        featureDefinitionId: result.featureDefinitionId,
        featureKey: result.featureKey,
        name: result.name || "",
        enabled: result.enabled,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt,
        updatedBy: result.updatedBy || undefined,
      }
      dispatch(addOrganizationOverride(override))
      // Update resolved state
      dispatch(
        updateResolvedFeature({
          featureKey: result.featureKey,
          enabled: result.enabled,
          source: "organization",
        })
      )
    },
    onError: (error: any) => {
      console.error("Failed to set organization feature override:", error)
    },
  })

  const [removeOrganizationFeatureOverrideMutation] = useMutation(gqlTypes.RemoveOrganizationFeatureOverrideDocument, {
    onCompleted: (data, options: any) => {
      const { variables } = options
      if (data.removeOrganizationFeatureOverride && variables) {
        dispatch(
          removeOrganizationOverride({
            featureKey: variables.featureKey,
            organizationId: variables.organizationId || activeOrganization?.organization.id || "",
          })
        )
        // Update resolved state to use global/default
        const globalFeature = global.find((f) => f.featureKey === variables.featureKey)
        const definition = definitions.find((d) => d.featureKey === variables.featureKey)
        const enabled = globalFeature?.enabled ?? definition?.defaultEnabled ?? false
        dispatch(
          updateResolvedFeature({
            featureKey: variables.featureKey,
            enabled,
            source: globalFeature ? "global" : "default",
          })
        )
      }
    },
    onError: (error: any) => {
      console.error("Failed to remove organization feature override:", error)
    },
  })

  // Action functions
  const refreshDefinitions = useCallback(() => {
    fetchDefinitions()
  }, [dispatch, fetchDefinitions])

  const refreshResolved = useCallback(() => {
    if (!activeOrganization) return
    fetchResolved()
  }, [dispatch, fetchResolved, activeOrganization])

  const refreshGlobal = useCallback(() => {
    fetchGlobal()
  }, [dispatch, fetchGlobal])

  const refreshOverrides = useCallback((organizationId?: string) => {
    const orgId = organizationId || activeOrganization?.organization.id
    if (!orgId) return
    fetchOverrides({
      variables: { organizationId: orgId },
    })
  }, [dispatch, fetchOverrides, activeOrganization])

  // Optimized refresh for admin interface - fetch definitions and global together
  const refreshAdminData = useCallback(() => {
    // Fetch both independently using refs for stable function references
    if (fetchDefinitionsRef.current) {
      fetchDefinitionsRef.current!()
    }
    if (fetchGlobalRef.current) {
      fetchGlobalRef.current!()
    }
  }, [])

  // Optimized refresh for normal users - single query for resolved states
  const refreshUserFeatures = useCallback(() => {
    // Use ref for stable function reference
    if (fetchUserFeatureStatesRef.current) {
      fetchUserFeatureStatesRef.current!()
    }
  }, [])

  const setGlobalToggle = useCallback(
    async (input: SetGlobalFeatureToggleInput) => {
      await setGlobalFeatureToggleMutation({
        variables: { input },
      })
    },
    [setGlobalFeatureToggleMutation]
  )

  const setOrganizationOverride = useCallback(
    async (input: SetOrganizationFeatureOverrideInput, organizationId?: string) => {
      const orgId = organizationId || activeOrganization?.organization.id
      if (!orgId) {
        throw new Error("No organization ID available")
      }

      await setOrganizationFeatureOverrideMutation({
        variables: {
          input,
          organizationId: orgId,
        },
      })
    },
    [setOrganizationFeatureOverrideMutation, activeOrganization]
  )

  const removeOverride = useCallback(
    async (featureKey: string, organizationId?: string) => {
      const orgId = organizationId || activeOrganization?.organization.id
      if (!orgId) {
        throw new Error("No organization ID available")
      }

      await removeOrganizationFeatureOverrideMutation({
        variables: {
          featureKey,
          organizationId: orgId,
        },
      })
    },
    [removeOrganizationFeatureOverrideMutation, activeOrganization]
  )

  // Convenience methods for backward compatibility
  const toggleGlobalFeature = useCallback(
    async (featureKey: string, enabled: boolean) => {
      return setGlobalToggle({ featureKey, enabled })
    },
    [setGlobalToggle]
  )

  const toggleOrganizationFeature = useCallback(
    async (featureKey: string, enabled: boolean, organizationId?: string) => {
      return setOrganizationOverride({ featureKey, enabled }, organizationId)
    },
    [setOrganizationOverride]
  )

  // Auto-fetch data when organization changes
  useEffect(() => {
    if (activeOrganization) {
      // Don't clear previous organization data eagerly — the new fetch will
      // overwrite resolved toggles atomically via setResolved(). Clearing first
      // creates a window where resolvedToggles is empty, causing sections to
      // mount with incomplete feature flags (race condition).
      // dispatch(clearOrganizationData())

      // Use optimized single query for normal users
      refreshUserFeatures()
    }
  }, [activeOrganization, dispatch, refreshUserFeatures])

  // Computed loading and error states
  const loading = definitionsLoading || resolvedLoading || globalLoading || overridesLoading
  const error = definitionsError || resolvedError || globalError || overridesError

  return {
    // Feature state checking
    isEnabled,
    getFeatureState,

    // Data
    definitions,
    resolved,
    global,
    overrides,

    // Loading states
    loading,
    definitionsLoading,
    resolvedLoading,
    globalLoading,
    overridesLoading,

    // Error states
    error,
    definitionsError,
    resolvedError,
    globalError,
    overridesError,

    // Actions
    refreshDefinitions,
    refreshResolved,
    refreshGlobal,
    refreshOverrides,
    refreshAdminData,
    refreshUserFeatures,
    setGlobalToggle,
    setOrganizationOverride,
    removeOverride,

    // Convenience methods
    toggleGlobalFeature,
    toggleOrganizationFeature,
  }
}

// Convenience hook for just checking if a feature is enabled
export const useFeatureEnabled = (featureKey: string): boolean => {
  return useSelector(selectIsFeatureEnabled(featureKey))
}

// Convenience hook for getting feature state with metadata
export const useFeatureState = (featureKey: string): ResolvedFeatureToggle | undefined => {
  return useSelector(selectFeatureState(featureKey))
}

// Simple hook that automatically loads features and provides easy access
export const useSimpleFeatures = () => {
  // Initialise feature loading
  const { refreshUserFeatures, loading, error } = useFeatureToggles()

  // Auto-load on mount if not already loaded
  const resolved = useSelector(selectResolvedFeatureToggles)
  const { activeOrganization } = useActiveOrganization()

  useEffect(() => {
    if (activeOrganization && resolved.length === 0 && !loading && !error) {
      refreshUserFeatures()
    }
  }, [activeOrganization, resolved.length, loading, error, refreshUserFeatures])

  return {
    isEnabled: (featureKey: string) => {
      const feature = resolved.find((f) => f.featureKey === featureKey)
      return feature?.enabled ?? false
    },
    getFeature: (featureKey: string) => {
      return resolved.find((f) => f.featureKey === featureKey)
    },
    loading,
    error,
    features: resolved,
  }
}
